import * as Moment from "moment";
import { IObjectTranslatable, RelationalActionTranslator, HtmlColorCodeTranslator, TextAlignKindsTranslator } from ".";
import { IDateItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import { IItem, DateItem } from "../../../../domain/document/items";
import { FuzzyDate } from "../../../../domain/system/FuzzyDate";
import { HisItemParameterTranslator } from "../../../his/ver2/HisItemParameterTranslator";
import { RelationalActionExtractor } from "./RelationalActionExtractor";
import { DateInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import { DateInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";

import {
  Point,
  Size,
  Rectangle,
  Color,
  TextStyle,
} from "../../../../domain/drawing";

export class DateItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IDateItemBean;
    const result = new DateItem(this.toValue(bean));
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.subName = bean.subName;
    result.groupKey = bean.groupKey;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, bean.height));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, bean.adjustedTextFontSize, bean.textFontName, bean.textLineHeight, false);
    result.format = bean.format;
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.enableNameGroupEmptyCheck = bean.nameGroupEmptyCheck;
    result.alertGroupKey = bean.alertGroup;
    result.userDefinitionKey = bean.sourceFile;
    result.userDefinitionSelectionMode = bean.selectionMode;
    result.itemName = bean.itemName;
    result.userDefinitionWindowKind = bean.windowType;
    result.isHisInfo = bean.isHisInfo;
    result.displayName = bean.displayName;
    result.groupName = bean.groupName;
    result.toolTip = bean.toolTip;
    result.separatorKind = bean.separatorType;
    result.hisItemParameter = HisItemParameterTranslator.toHisItemParameter(bean.hisItemParameter);
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.enableApproximateInput = !!bean.approximateInput;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.calculateInformations = RelationalActionExtractor.extractCalculateParameters(result.actions);
    result.isFreeInputable = bean.supportedFreeText;
    result.isCalendarInputtable = !!bean.supportedCalendar;
    result.inputMethodOptionKind = DateInputMethodOptionKinds.toInputMethodOptionKinds(bean);
    result.inputMethodKind = this.toInputMethodKind(bean, result.inputMethodOptionKind);
    result.initialInputMethodKind = bean.inputMethod; // v1では初回記載開始時の初期入力方法。カレンダー入力 or 平成直接入力 徳大では使用しない
    result.enabledAutoAdjustFontSize = !!bean.enabledAutoAdjustFontSize;
    result.autoNotePageSize = bean.autoNotePageSize;
    result.autoNotePageKind = bean.autoNotePageType;
    result.isInitial = bean.isInitial;
    result.initialSettingKind = bean.initialSetting;
    result.shouldSelectHospitalizationInitialValueHospitalizedOnly = !!bean.initialAdmission;
    result.isDialogMultiSelectable = !!bean.enabledHisMultiSelect;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    result.paintStyleOnEditing = bean.isDisplayBackColorSpecified
      ? HtmlColorCodeTranslator.toPaintStyleFill(bean.displayBackColor)
      : null;
    result.linkedValue = bean.linkedValue;
    result.userDefineLinkActions = RelationalActionExtractor.extractUserDefineLinkParameters(result.actions);
    result.userDefineSearchActions = RelationalActionExtractor.extractUserDefineSearchParameters(result.actions);
    result.dateLinkDestinations = RelationalActionExtractor.extractObjectLinkParameters(result.actions);
    result.manualFormat = bean.manualFormat;
    result.manualFormatLanguage = bean.manualFormatLanguage;
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.isNaturalViewEnable = bean.naturalView;
    result.naturalText = bean.naturalText;
    return result;
  }

  private toValue(bean: IDateItemBean): FuzzyDate {
    if (bean.isEmpty) {
      return new FuzzyDate(null, bean.isTime);
    }
    const linkedDateTime = this.toLinkedDateTime(bean.linkedDateTime);
    return this.isDate(bean)
      ? new FuzzyDate(this.toDate(bean), bean.isTime, linkedDateTime)
      : new FuzzyDate(bean.text, bean.isTime, linkedDateTime);
  }

  private isDate(bean: IDateItemBean): boolean {
    return !!bean
      && !!bean.value
      && !!bean.value.match(/^[0-9]{14}$/);
  }

  private toDate(bean: IDateItemBean): Date {
    // 古いバージョンでは00000000000000が日付選択されていない状態を示していたが、もう来ない見込み
    // 念のため残している
    return (bean.value === "00000000000000")
      ? null
      : this.toDateFromDateTimeText(bean.value);
  }

  private toDateFromDateTimeText(value: string): Date {
    return new Date(
      Number(value.substr(0, 4)),
      Number(value.substr(4, 2)) - 1,
      Number(value.substr(6, 2)),
      Number(value.substr(8, 2)),
      Number(value.substr(10, 2)),
      Number(value.substr(12, 2))
    );
  }

  /**
   * あいまい日時に関連する日時情報
   */
  private toLinkedDateTime(value: string): Date {
    return (!value || value.match(/^0+$/) || !value.match(/^[0-9]+$/))
      ? null
      : this.toDateFromDateTimeText((value + "00000000000000").substr(0, 14));
  }

  private toInputMethodKind(bean: IDateItemBean, optionKind: DateInputMethodOptionKinds): DateInputMethodKinds {
    if (bean.enabledFreeText) { return DateInputMethodKinds.FreeInput; }
    if (bean.enabledCalendar) { return DateInputMethodKinds.Calendar; }

    return DateInputMethodOptionKinds.getDefualtInputMethodKind(optionKind);
  }

  toBean(item: IItem): ITypeDefinable {
    const target = item as DateItem;
    const bean: IDateItemBean = {
      type: BeanTypes.Date,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      height: target.geometry.rectangle.height,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      name: target.name,
      subName: target.subName,
      groupKey: target.groupKey,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textFontName: target.textStyle.fontFamily,
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      displayBackColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyleOnEditing),
      text: target.text(),
      value: target.value.isFuzzy ? "" : target.value.toString("YYYYMMDDHHmmss"),
      format: target.format,
      isTime: target.value.isTime,
      relationalItems: null,  // TODO:返却する必要ある？
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      nameGroupEmptyCheck: target.enableNameGroupEmptyCheck,
      alertGroup: target.alertGroupKey,
      sourceFile: target.userDefinitionKey,
      selectionMode: target.userDefinitionSelectionMode,
      itemName: target.itemName,
      windowType: target.userDefinitionWindowKind,
      isHisInfo: target.isHisInfo,
      displayName: target.displayName,
      groupName: target.groupName,
      toolTip: target.toolTip,
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      separatorType: target.separatorKind,
      hisItemParameter: null, // 返却する必要なし
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      approximateInput: target.enableApproximateInput,
      inputMethod: target.initialInputMethodKind,
      supportedFreeText: target.isFreeInputable,
      supportedCalendar: target.isCalendarInputtable,
      enabledFreeText: target.shouldInputFree,
      enabledCalendar: target.shouldInputCalendar,
      enabledAutoAdjustFontSize: target.enabledAutoAdjustFontSize,
      autoNotePageSize: target.autoNotePageSize,
      autoNotePageType: target.autoNotePageKind,
      adjustedTextFontSize: target.textStyle.adjustedFontSize,
      isInitial: target.isInitial,
      initialSetting: target.initialSettingKind,
      initialAdmission: target.shouldSelectHospitalizationInitialValueHospitalizedOnly,
      enabledHisMultiSelect: target.isDialogMultiSelectable,
      isDisplayBackColorSpecified: !!target.paintStyleOnEditing,
      isEmpty: !target.shouldInputFree && target.isValueEmpty,
      linkedValue: target.linkedValue,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      manualFormat: target.manualFormat,
      manualFormatLanguage: target.manualFormatLanguage,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      naturalView: target.isNaturalViewEnable,
      naturalText: target.naturalText,
      isManualInputSuppressed: null,  // 返却する必要なし
      linkedDateTime: target.value.linkedDate ? Moment(target.value.linkedDate).format("YYYYMMDDHHmmss") : null,
    };
    return bean;
  }
}
